import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { useOrganizations } from '../../hooks/useOrganizations';
import { QUOTE_NUMBER_CONFIG_DEFAULTS } from '../../types/admin';

// ── Page ───────────────────────────────────────────────────────────────────

export function CreateOrganizationPage() {
  const navigate = useNavigate();
  const { createOrg } = useOrganizations();

  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [isDemo, setIsDemo] = useState(false);

  // Feature flags — allowed flags for the new org
  const [featureCommercialMovingQuotes, setFeatureCommercialMovingQuotes] = useState(true);
  const [featureResidentialMovingQuotes, setFeatureResidentialMovingQuotes] = useState(true);
  const [featureLeadPipeline, setFeatureLeadPipeline] = useState(true);
  const [featureDispatch, setFeatureDispatch] = useState(true);
  const [featureJobs, setFeatureJobs] = useState(true);
  const [featureClientPortal, setFeatureClientPortal] = useState(true);
  const [featureOnlineLeadCapture, setFeatureOnlineLeadCapture] = useState(true);
  const [featureQuotePdfs, setFeatureQuotePdfs] = useState(true);
  const [featureSmartMovingIntegration, setFeatureSmartMovingIntegration] = useState(false);
  const [featureFleetManagement, setFeatureFleetManagement] = useState(true);
  const [featureCustomerPayments, setFeatureCustomerPayments] = useState(true);
  const [featureStripeConnect, setFeatureStripeConnect] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) { setError('Organization name is required.'); return; }
    setError('');
    setSubmitting(true);
    try {
      const org = await createOrg({
        name: name.trim(),
        // Default settings — applied in the background
        // Per-move-type quote defaults are seeded from platform defaults on the backend
        defaultCurrency: 'USD',
        defaultUnits: 'imperial',
        efficiencyMultiplier: 1,
        defaultTravelTimeRate: 'one_hour_standard',
        quoteNumberConfig: { ...QUOTE_NUMBER_CONFIG_DEFAULTS },
        defaultQuoteValidityDays: 30,
        ...(isDemo ? { isDemo: true } : {}),
        // Feature flags — set both enabled and allowed
        featureCommercialMovingQuotes, allowedFeatureCommercialMovingQuotes: featureCommercialMovingQuotes,
        featureResidentialMovingQuotes, allowedFeatureResidentialMovingQuotes: featureResidentialMovingQuotes,
        featureLeadPipeline, allowedFeatureLeadPipeline: featureLeadPipeline,
        featureDispatch, allowedFeatureDispatch: featureDispatch,
        featureJobs, allowedFeatureJobs: featureJobs,
        featureClientPortal, allowedFeatureClientPortal: featureClientPortal,
        featureOnlineLeadCapture, allowedFeatureOnlineLeadCapture: featureOnlineLeadCapture,
        featureQuotePdfs, allowedFeatureQuotePdfs: featureQuotePdfs,
        featureSmartMovingIntegration, allowedFeatureSmartMovingIntegration: featureSmartMovingIntegration,
        featureFleetManagement, allowedFeatureFleetManagement: featureFleetManagement,
        featureCustomerPayments, allowedFeatureCustomerPayments: featureCustomerPayments,
        featureStripeConnect, allowedFeatureStripeConnect: featureStripeConnect,
      });
      navigate(`/admin/platform-tools/organizations/${org.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create organization.');
      setSubmitting(false);
    }
  }

  return (
    <div className="px-4 pt-6 pb-12 md:px-8 md:max-w-2xl md:mx-auto">
      {/* Back */}
      <button
        type="button"
        onClick={() => navigate('/admin/platform-tools/organizations')}
        className="flex items-center gap-1 text-small text-slate hover:text-midnight mb-4
                   transition-colors duration-150 -ml-1"
      >
        <ChevronLeft size={16} />
        Organizations
      </button>

      <h1 className="font-display text-h1 text-midnight mb-6">New Organization</h1>

      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        {error && (
          <div className="px-4 py-3 rounded-lg bg-rose/10 border border-rose/30 text-small text-rose">
            {error}
          </div>
        )}

        {/* Org name */}
        <div>
          <label
            htmlFor="org-name"
            className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1"
          >
            Organization Name <span className="text-rose">*</span>
          </label>
          <input
            id="org-name"
            type="text"
            placeholder="Acme Moving Co."
            value={name}
            onChange={e => setName(e.target.value)}
            className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                       text-small text-midnight placeholder:text-slate
                       focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                       transition-colors duration-150"
          />
        </div>

        {/* Demo Org Toggle */}
        <div className="flex items-center justify-between bg-white border border-stone rounded-xl px-4 py-3.5">
          <div>
            <p className="text-[13px] font-semibold text-midnight">Demo Organization</p>
            <p className="text-[12px] text-slate mt-0.5 leading-snug">
              Demo orgs can be excluded from production stats on the Platform Dashboard.
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={isDemo}
            onClick={() => setIsDemo(v => !v)}
            className={`relative shrink-0 ml-4 w-11 h-6 rounded-full transition-colors duration-200
              ${isDemo ? 'bg-plum' : 'bg-stone'}`}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200
                ${isDemo ? 'translate-x-5' : 'translate-x-0'}`}
            />
          </button>
        </div>

        {/* Feature Flags */}
        <div className="border border-stone rounded-xl overflow-hidden">
          <div className="px-4 py-3 bg-mist border-b border-stone">
            <p className="text-[13px] font-semibold text-midnight">Allowed Features</p>
            <p className="text-[12px] text-slate mt-0.5 leading-snug">
              Controls which features this organization is permitted to use.
            </p>
          </div>
          <div className="divide-y divide-stone">
            {([
              { label: 'Commercial Moving Quotes', desc: 'Commercial moving quote functionality.', value: featureCommercialMovingQuotes, set: setFeatureCommercialMovingQuotes },
              { label: 'Residential Moving Quotes', desc: 'Residential moving quote functionality.', value: featureResidentialMovingQuotes, set: setFeatureResidentialMovingQuotes },
              { label: 'Operations (Dispatch + Jobs)', desc: 'Dispatch screen, Jobs page, mover role, and Dashboard Jobs tab.', value: featureDispatch, set: (v: boolean | ((prev: boolean) => boolean)) => { const newVal = typeof v === 'function' ? v(featureDispatch) : v; setFeatureDispatch(newVal); setFeatureJobs(newVal); } },
              { label: 'Fleet Management', desc: 'Truck and warehouse fleet tracking, location management, and dispatch vehicle assignment.', value: featureFleetManagement, set: setFeatureFleetManagement },
              { label: 'Lead Pipeline', desc: 'Leads page, lead tracking, and follow-up management.', value: featureLeadPipeline, set: setFeatureLeadPipeline },
              { label: 'Client Portal', desc: 'Online Quote buttons on quote versions.', value: featureClientPortal, set: setFeatureClientPortal },
              { label: 'Online Lead Capture', desc: 'Public booking form for capturing leads.', value: featureOnlineLeadCapture, set: setFeatureOnlineLeadCapture },
              { label: 'Quote PDFs', desc: 'Generate PDFs when creating quote versions.', value: featureQuotePdfs, set: setFeatureQuotePdfs },
              { label: 'Customer Payments', desc: 'Configure and accept payments from customers.', value: featureCustomerPayments, set: setFeatureCustomerPayments },
              { label: 'Stripe Connect', desc: 'Allow organization to connect their Stripe account for payment processing.', value: featureStripeConnect, set: setFeatureStripeConnect },
              { label: 'SmartMoving Integration', desc: 'Allow this org to connect and sync with SmartMoving CRM.', value: featureSmartMovingIntegration, set: setFeatureSmartMovingIntegration },
            ] as const).map(flag => (
              <div key={flag.label} className="flex items-center justify-between px-4 py-3">
                <div>
                  <p className="text-[13px] font-semibold text-midnight">{flag.label}</p>
                  <p className="text-[12px] text-slate mt-0.5 leading-snug">{flag.desc}</p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={flag.value}
                  onClick={() => flag.set(v => !v)}
                  className={`relative shrink-0 ml-4 w-11 h-6 rounded-full transition-colors duration-200
                    ${flag.value ? 'bg-plum' : 'bg-stone'}`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200
                      ${flag.value ? 'translate-x-5' : 'translate-x-0'}`}
                  />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Self-service note */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
          <p className="text-[13px] text-blue-800 leading-relaxed">
            When the new organization's admin creates their account and logs in, they'll be able
            to configure their organization and subscribe to a plan through their own billing dashboard.
          </p>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={submitting}
          className="w-full h-12 rounded-xl bg-plum text-white text-small font-semibold
                     hover:bg-rose active:opacity-80 disabled:opacity-50
                     transition-colors duration-150"
        >
          {submitting ? 'Creating…' : 'Create Organization'}
        </button>
      </form>
    </div>
  );
}
